<template>
  <div class="srp">
    <!-- Header row: session count -->
    <div class="srp-header">
      <div class="srp-header-info">
        <span v-if="isLoading" class="srp-muted">Loading sessions…</span>
        <span v-else-if="errorMessage" class="srp-error">{{ errorMessage }}</span>
        <span v-else-if="sessions.length" class="srp-count">
          {{ sessions.length }} session{{ sessions.length === 1 ? '' : 's' }}
          <template v-if="isEnrichingUsers"> · enriching users…</template>
        </span>
        <span v-else class="srp-muted">No sessions recorded yet.</span>
      </div>
    </div>

    <p v-if="downloadError" class="srp-error srp-download-error">{{ downloadError }}</p>

    <div v-if="!isLoading && !errorMessage && sessions.length" class="srp-table-scroll">
      <table class="srp-table">
        <thead>
          <tr>
            <th style="width: 2rem;"></th>
            <th :aria-sort="ariaSort('user')">
              <button type="button" class="srp-sort-btn" @click="setSort('user')">
                User <span class="srp-sort-ind" :class="{ 'srp-sort-ind--active': sortKey === 'user' }">{{ sortIndicator('user') }}</span>
              </button>
            </th>
            <th :aria-sort="ariaSort('started')">
              <button type="button" class="srp-sort-btn" @click="setSort('started')">
                Started <span class="srp-sort-ind" :class="{ 'srp-sort-ind--active': sortKey === 'started' }">{{ sortIndicator('started') }}</span>
              </button>
            </th>
            <th :aria-sort="ariaSort('completed')">
              <button type="button" class="srp-sort-btn" @click="setSort('completed')">
                Completed <span class="srp-sort-ind" :class="{ 'srp-sort-ind--active': sortKey === 'completed' }">{{ sortIndicator('completed') }}</span>
              </button>
            </th>
            <th :aria-sort="ariaSort('duration')">
              <button type="button" class="srp-sort-btn" @click="setSort('duration')">
                Duration <span class="srp-sort-ind" :class="{ 'srp-sort-ind--active': sortKey === 'duration' }">{{ sortIndicator('duration') }}</span>
              </button>
            </th>
            <th :aria-sort="ariaSort('stages')">
              <button type="button" class="srp-sort-btn" @click="setSort('stages')">
                Stages <span class="srp-sort-ind" :class="{ 'srp-sort-ind--active': sortKey === 'stages' }">{{ sortIndicator('stages') }}</span>
              </button>
            </th>
            <th v-if="anyScored" :aria-sort="ariaSort('score')">
              <button type="button" class="srp-sort-btn" @click="setSort('score')">
                Score <span class="srp-sort-ind" :class="{ 'srp-sort-ind--active': sortKey === 'score' }">{{ sortIndicator('score') }}</span>
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="session in sortedSessions" :key="session.sessionId">
            <tr class="srp-session-row" @click="toggleExpand(session.sessionId)">
              <td class="srp-toggle-cell">
                <span :class="['srp-chev', { 'srp-chev--open': expanded.has(session.sessionId) }]" aria-hidden="true">▸</span>
              </td>
              <td>{{ formatUser(session) }}</td>
              <td>{{ formatDateTime(session.startedAt) }}</td>
              <td>{{ formatDateTime(session.completedAt) }}</td>
              <td>{{ formatDuration(session) }}</td>
              <td>{{ session.stageOrder || 0 }} / {{ session.totalStages || '—' }}</td>
              <td v-if="anyScored">{{ session.score }} / {{ session.totalQuestions || session.totalStages || '—' }}</td>
            </tr>
            <tr v-if="expanded.has(session.sessionId)" class="srp-detail-row">
              <td :colspan="anyScored ? 7 : 6" class="srp-detail-cell">
                <p v-if="detailError.get(session.sessionId)" class="srp-error">
                  {{ detailError.get(session.sessionId) }}
                </p>
                <p v-else-if="!detailCache.has(session.sessionId)" class="srp-muted">
                  Loading stages…
                </p>
                <div v-else class="srp-stages">
                  <!-- Group by round when multiple rounds present -->
                  <template v-for="(group, roundLabel) in groupedStages(detailCache.get(session.sessionId)!.stages)" :key="roundLabel">
                    <p v-if="hasMultipleRounds(detailCache.get(session.sessionId)!.stages)" class="srp-round-label">
                      {{ roundLabel }}
                    </p>
                    <div
                      v-for="stage in group"
                      :key="stage.sk || `${stage.questionId}-${stage.stageOrder}`"
                      class="srp-stage"
                    >
                      <div class="srp-stage-head">
                        <strong>Stage {{ stage.stageOrder ?? '—' }}</strong>
                        <span class="srp-stage-meta">
                          {{ stage.responseType || '' }}
                          <template v-if="stage.recordedAt"> · {{ formatDateTime(stage.recordedAt) }}</template>
                        </span>
                      </div>
                      <div class="srp-stage-body">
                        <span class="srp-stage-q">Q: {{ resolveQuestionText(stage.questionId) }}</span>
                        <span class="srp-stage-a">A: {{ describeAnswer(stage) }}</span>
                      </div>
                    </div>
                    <p v-if="!group.length" class="srp-muted">No stage data for this session.</p>
                  </template>
                  <p v-if="!Object.keys(groupedStages(detailCache.get(session.sessionId)!.stages)).length" class="srp-muted">
                    No stage data for this session.
                  </p>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="sessions.length && !errorMessage" class="srp-footer">
      <p class="srp-footer-note">
        Click a row to expand. Download CSV produces one row per session (wide). "Download stages (long)" is the legacy per-stage format.
      </p>
      <div class="srp-footer-actions">
        <button
          type="button"
          class="secondary-button secondary-button-small"
          :disabled="isDownloading || !sessions.length"
          @click="downloadWideCsv"
        >
          {{ isDownloading ? 'Preparing…' : 'Download CSV' }}
        </button>
        <button
          type="button"
          class="secondary-button secondary-button-small"
          :disabled="isDownloading || !sessions.length"
          @click="downloadLongCsv"
        >
          Download stages (long)
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import {
  campaignsService,
  type ManagedExperienceSession,
  type ManagedExperienceSessionDetail,
  type ManagedCampaignUser,
} from '@/services/campaigns'

const props = defineProps<{
  campaignId: string | null
  campaignName?: string | null
}>()

// ─── State ──────────────────────────────────────────────────────────────────

const isLoading = ref(false)
const isEnrichingUsers = ref(false)
const isDownloading = ref(false)
const errorMessage = ref('')
const downloadError = ref('')
const sessions = ref<ManagedExperienceSession[]>([])
const expanded = reactive(new Set<string>())
const detailCache = reactive(new Map<string, ManagedExperienceSessionDetail>())
const detailError = reactive(new Map<string, string>())

// User identity map: userId → ManagedCampaignUser
const userMap = reactive(new Map<string, ManagedCampaignUser>())

// Flat question map: questionId → { text, choices }
type QuestionMeta = { text: string; choices: Array<{ id: string; label: string }> }
const questionMap = reactive(new Map<string, QuestionMeta>())

// ─── Helpers ─────────────────────────────────────────────────────────────────

const anyScored = computed(() =>
  sessions.value.some((s) => (s.totalQuestions ?? 0) > 0 || s.score > 0),
)

// ─── Sorting ──────────────────────────────────────────────────────────────────

type SortKey = 'user' | 'started' | 'completed' | 'duration' | 'stages' | 'score'
type SortDir = 'asc' | 'desc'

// First-click direction per column (matches the table's natural reading).
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  user: 'asc', // A → Z
  started: 'desc', // newest → oldest
  completed: 'desc', // newest → oldest
  duration: 'asc', // shortest → longest
  stages: 'desc', // most progress → least
  score: 'desc', // highest → lowest
}

const sortKey = ref<SortKey>('started')
const sortDir = ref<SortDir>(DEFAULT_DIR.started)

function setSort(key: SortKey) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = DEFAULT_DIR[key]
  }
}

function ariaSort(key: SortKey): 'ascending' | 'descending' | 'none' {
  if (sortKey.value !== key) return 'none'
  return sortDir.value === 'asc' ? 'ascending' : 'descending'
}

function sortIndicator(key: SortKey): string {
  // Inactive columns show a faint down-caret (styled via opacity) as a
  // consistent "sortable" affordance; the active column shows its direction.
  if (sortKey.value !== key) return '▼'
  return sortDir.value === 'asc' ? '▲' : '▼'
}

// Returns the comparable value plus a `missing` flag. Missing values (no
// completion / no duration) always sort to the bottom, regardless of direction.
function sortValue(s: ManagedExperienceSession, key: SortKey): { v: number | string; missing: boolean } {
  switch (key) {
    case 'user':
      return { v: formatUser(s).toLowerCase(), missing: false }
    case 'started': {
      const t = s.startedAt ? Date.parse(s.startedAt) : NaN
      return { v: t, missing: !Number.isFinite(t) }
    }
    case 'completed': {
      const t = s.completedAt ? Date.parse(s.completedAt) : NaN
      return { v: t, missing: !Number.isFinite(t) }
    }
    case 'duration': {
      const d = durationSeconds(s)
      return { v: typeof d === 'number' ? d : NaN, missing: d === '' }
    }
    case 'stages': {
      const total = s.totalStages || 0
      const ratio = total > 0 ? (s.stageOrder || 0) / total : (s.stageOrder || 0)
      return { v: ratio, missing: false }
    }
    case 'score':
      return { v: s.score ?? 0, missing: false }
  }
}

const sortedSessions = computed(() => {
  const arr = sessions.value.slice()
  const dir = sortDir.value === 'asc' ? 1 : -1
  const key = sortKey.value
  arr.sort((a, b) => {
    const va = sortValue(a, key)
    const vb = sortValue(b, key)
    if (va.missing && vb.missing) return 0
    if (va.missing) return 1 // a sinks
    if (vb.missing) return -1 // b sinks
    if (typeof va.v === 'string' || typeof vb.v === 'string') {
      return String(va.v).localeCompare(String(vb.v)) * dir
    }
    return ((va.v as number) - (vb.v as number)) * dir
  })
  return arr
})

type StageRecord = {
  sk?: string
  stageOrder?: number
  questionOrder?: number
  questionId?: string
  round?: string | number
  selectedChoiceId?: string
  selectedChoiceIds?: string[]
  textResponse?: string
  ratingValue?: number
  booleanValue?: boolean
  responseType?: string
  correct?: boolean
  recordedAt?: string
}

function redactEmail(email: string | null | undefined): string {
  if (!email) return '—'
  const at = email.indexOf('@')
  if (at < 0) return email.slice(0, 4) + '***'
  const local = email.slice(0, at)
  const domain = email.slice(at)
  if (local.length <= 4) return local + '***' + domain
  return local.slice(0, 4) + '***' + domain
}

function formatUser(s: ManagedExperienceSession): string {
  // Try direct id match first, then externalId fallback
  const user = userMap.get(s.userId) ?? findUserByExternalId(s.userId)
  if (!user) {
    // Fall back to truncated userId
    return s.userId.slice(0, 8) + '…'
  }
  const email = redactEmail(user.email)
  const nameParts = [user.firstName, user.lastName].filter(Boolean)
  if (nameParts.length) return `${email} · ${nameParts.join(' ')}`
  return email
}

function findUserByExternalId(externalId: string): ManagedCampaignUser | undefined {
  for (const [, u] of userMap) {
    if (u.externalId === externalId) return u
  }
  return undefined
}

function resolveQuestionText(questionId?: string): string {
  if (!questionId) return '—'
  const q = questionMap.get(questionId)
  return q?.text || questionId
}

function describeAnswer(stage: StageRecord): string {
  if (typeof stage.textResponse === 'string' && stage.textResponse.length) return stage.textResponse

  if (Array.isArray(stage.selectedChoiceIds) && stage.selectedChoiceIds.length) {
    const labels = stage.selectedChoiceIds.map((cid) => resolveChoiceLabel(stage.questionId, cid))
    return labels.join(' | ')
  }

  if (typeof stage.selectedChoiceId === 'string' && stage.selectedChoiceId.length) {
    return resolveChoiceLabel(stage.questionId, stage.selectedChoiceId)
  }

  if (typeof stage.ratingValue === 'number') {
    const q = stage.questionId ? questionMap.get(stage.questionId) : undefined
    const max = (q as any)?.ratingScale?.max ?? 5
    return `${stage.ratingValue}/${max}`
  }

  if (typeof stage.booleanValue === 'boolean') return stage.booleanValue ? 'Yes' : 'No'
  return '—'
}

function resolveChoiceLabel(questionId: string | undefined, choiceId: string): string {
  if (!questionId) return choiceId
  const q = questionMap.get(questionId)
  if (!q) return choiceId
  const choice = q.choices.find((c) => c.id === choiceId)
  return choice?.label ?? choiceId
}

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed)
}

const formatDuration = (s: ManagedExperienceSession): string => {
  if (!s.startedAt || !s.completedAt) return '—'
  const start = Date.parse(s.startedAt)
  const end = Date.parse(s.completedAt)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '—'
  const seconds = Math.max(0, Math.round((end - start) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${seconds % 60}s`
}

const durationSeconds = (s: ManagedExperienceSession): number | '' => {
  if (!s.startedAt || !s.completedAt) return ''
  const start = Date.parse(s.startedAt)
  const end = Date.parse(s.completedAt)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return ''
  return Math.max(0, Math.round((end - start) / 1000))
}

// Group stages by round field for the detail view
function groupedStages(rawStages: Array<Record<string, unknown>>): Record<string, StageRecord[]> {
  const stages = (rawStages as StageRecord[]).slice().sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0))
  const groups: Record<string, StageRecord[]> = {}
  for (const stage of stages) {
    const key = stage.round != null ? `Round ${stage.round}` : 'Round 1'
    if (!groups[key]) groups[key] = []
    groups[key].push(stage)
  }
  return groups
}

function hasMultipleRounds(rawStages: Array<Record<string, unknown>>): boolean {
  const rounds = new Set((rawStages as StageRecord[]).map((s) => s.round ?? 1))
  return rounds.size > 1
}

// ─── Data loading ─────────────────────────────────────────────────────────────

const refresh = async () => {
  if (!props.campaignId) {
    sessions.value = []
    return
  }
  isLoading.value = true
  errorMessage.value = ''
  expanded.clear()
  detailCache.clear()
  detailError.clear()
  userMap.clear()
  questionMap.clear()
  try {
    sessions.value = await campaignsService.listExperienceSessions(props.campaignId)
    // Fire user + question pack enrichment in parallel (best-effort; failures logged)
    void enrichUsers(props.campaignId)
    void enrichQuestionPack(props.campaignId)
  } catch (err) {
    console.warn('[SurveyResponsesPanel] Failed to load sessions', err)
    errorMessage.value = 'Unable to load sessions right now.'
  } finally {
    isLoading.value = false
  }
}

// Paginate through all users and build the userId → user map.
const enrichUsers = async (campaignId: string) => {
  isEnrichingUsers.value = true
  try {
    let lastKey: string | null | undefined = undefined
    let mismatches = 0
    let totalLoaded = 0
    do {
      const page = await campaignsService.listCampaignUsers(campaignId, 1000, lastKey ?? undefined)
      for (const u of page.items) {
        userMap.set(u.id, u)
        totalLoaded++
      }
      lastKey = page.lastKey
    } while (lastKey)

    // Verify join quality: count sessions that couldn't be matched
    for (const s of sessions.value) {
      if (!userMap.has(s.userId) && !findUserByExternalId(s.userId)) mismatches++
    }
    if (mismatches > 0) {
      console.warn(
        `[SurveyResponsesPanel] ${mismatches} of ${sessions.value.length} sessions could not be matched to a user profile. ` +
        `Loaded ${totalLoaded} users. Falling back to truncated userId display for unmatched rows.`,
      )
    }
  } catch (err) {
    console.warn('[SurveyResponsesPanel] User enrichment failed (non-fatal):', err)
  } finally {
    isEnrichingUsers.value = false
  }
}

// Load the question pack and flatten all questions into the map.
const enrichQuestionPack = async (campaignId: string) => {
  try {
    const draft = await campaignsService.getCampaignEditDraft(campaignId)
    const pack = draft.questionPack as any
    if (!pack) return

    // Walk the canonical nested structure: experience.rounds[].stages[].questionSet.questions[]
    const rounds: any[] = Array.isArray(pack?.experience?.rounds) ? pack.experience.rounds : []
    for (const round of rounds) {
      const stages: any[] = Array.isArray(round.stages) ? round.stages : []
      for (const stage of stages) {
        const questions: any[] = Array.isArray(stage.questionSet?.questions) ? stage.questionSet.questions : []
        for (const q of questions) {
          if (!q?.id) continue
          questionMap.set(q.id, {
            text: q.text || q.prompt?.text || q.id,
            choices: Array.isArray(q.choices) ? q.choices.map((c: any) => ({ id: c.id, label: c.label ?? c.id })) : [],
          })
        }
      }
    }

    // Also walk a flat top-level questions[] if present (older pack shapes)
    const flatQuestions: any[] = Array.isArray(pack?.questions) ? pack.questions : []
    for (const q of flatQuestions) {
      if (!q?.id || questionMap.has(q.id)) continue
      questionMap.set(q.id, {
        text: q.text || q.prompt?.text || q.id,
        choices: Array.isArray(q.choices) ? q.choices.map((c: any) => ({ id: c.id, label: c.label ?? c.id })) : [],
      })
    }
  } catch (err) {
    console.warn('[SurveyResponsesPanel] Question pack enrichment failed (non-fatal):', err)
  }
}

const ensureDetail = async (sessionId: string): Promise<ManagedExperienceSessionDetail | null> => {
  if (!props.campaignId) return null
  const cached = detailCache.get(sessionId)
  if (cached) return cached
  try {
    const detail = await campaignsService.getExperienceSessionDetail(props.campaignId, sessionId)
    detailCache.set(sessionId, detail)
    detailError.delete(sessionId)
    return detail
  } catch (err) {
    console.warn('[SurveyResponsesPanel] Failed to load session detail', err)
    detailError.set(sessionId, 'Unable to load stages for this session.')
    return null
  }
}

const toggleExpand = async (sessionId: string) => {
  if (expanded.has(sessionId)) {
    expanded.delete(sessionId)
    return
  }
  expanded.add(sessionId)
  await ensureDetail(sessionId)
}

// ─── CSV helpers ──────────────────────────────────────────────────────────────

const csvEscape = (value: unknown): string => {
  const s = value === null || value === undefined ? '' : String(value)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

const triggerDownload = (csv: string, suffix: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const base = `${props.campaignName || props.campaignId}`.replace(/[^a-z0-9_.-]/gi, '_')
  link.download = `${base}-${suffix}.csv`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

// Ensure all session details are loaded (serial to avoid hammering the backend)
const ensureAllDetails = async () => {
  for (const session of sessions.value) {
    if (!detailCache.has(session.sessionId)) {
      await ensureDetail(session.sessionId)
    }
  }
}

// ─── Wide CSV (one row per session) ──────────────────────────────────────────

const downloadWideCsv = async () => {
  if (!props.campaignId || isDownloading.value || !sessions.value.length) return
  isDownloading.value = true
  downloadError.value = ''
  try {
    await ensureAllDetails()

    // Build ordered question columns from the pack map (sorted by first-seen order)
    // We use the order sessions are encountered, which approximates question order.
    const orderedQuestionIds: string[] = []
    const seenQids = new Set<string>()
    for (const session of sessions.value) {
      const stages = (detailCache.get(session.sessionId)?.stages ?? []) as StageRecord[]
      for (const stage of stages.sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0))) {
        if (stage.questionId && !seenQids.has(stage.questionId)) {
          seenQids.add(stage.questionId)
          orderedQuestionIds.push(stage.questionId)
        }
      }
    }

    // Question column headers: "Q{N}: {text}"
    const questionHeaders = orderedQuestionIds.map((qid, idx) => {
      const text = resolveQuestionText(qid)
      return `Q${idx + 1}: ${text}`
    })

    const header = [
      'session_id',
      'email',
      'firstName',
      'lastName',
      'mobileNumber',
      'status',
      'startedAt',
      'completedAt',
      'duration_seconds',
      'score',
      'total_questions',
      'round',
      ...questionHeaders,
    ]

    const lines = [header.map(csvEscape).join(',')]

    for (const session of sessions.value) {
      const user = userMap.get(session.userId) ?? findUserByExternalId(session.userId) ?? null
      const stages = (detailCache.get(session.sessionId)?.stages ?? []) as StageRecord[]
      const sortedStages = stages.slice().sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0))

      // Build answer cell per question id
      const answerCells: Record<string, string> = {}
      for (const stage of sortedStages) {
        if (!stage.questionId) continue
        answerCells[stage.questionId] = describeAnswer(stage)
      }

      // Determine round — use round field from first stage if available
      const firstStageWithRound = sortedStages.find((s) => s.round != null)
      const roundVal = firstStageWithRound?.round ?? ''

      const row = [
        session.sessionId,
        user?.email ?? '',
        user?.firstName ?? '',
        user?.lastName ?? '',
        user?.mobileNumber ?? '',
        session.status,
        session.startedAt ?? '',
        session.completedAt ?? '',
        durationSeconds(session),
        session.score ?? '',
        session.totalQuestions ?? session.totalStages ?? '',
        roundVal,
        ...orderedQuestionIds.map((qid) => answerCells[qid] ?? ''),
      ]
      lines.push(row.map(csvEscape).join(','))
    }

    triggerDownload(lines.join('\n'), 'responses-wide')
  } catch (err) {
    console.warn('[SurveyResponsesPanel] Wide CSV download failed', err)
    downloadError.value = 'Unable to generate CSV right now.'
  } finally {
    isDownloading.value = false
  }
}

// ─── Long CSV (legacy: one row per stage) ────────────────────────────────────

const downloadLongCsv = async () => {
  if (!props.campaignId || isDownloading.value || !sessions.value.length) return
  isDownloading.value = true
  downloadError.value = ''
  try {
    await ensureAllDetails()

    const header = [
      'session_id',
      'campaign_id',
      'user_id',
      'session_status',
      'session_started_at',
      'session_completed_at',
      'session_duration_seconds',
      'session_score',
      'session_total_stages',
      'stage_order',
      'question_id',
      'response_type',
      'selected_choice_id',
      'selected_choice_ids',
      'text_response',
      'rating_value',
      'boolean_value',
      'correct',
      'recorded_at',
    ]
    const lines = [header.map(csvEscape).join(',')]

    for (const session of sessions.value) {
      const stages = ((detailCache.get(session.sessionId)?.stages ?? []) as StageRecord[])
      const stageRows = stages.length ? stages : [null]
      const dur = durationSeconds(session)
      for (const stage of stageRows) {
        lines.push(
          [
            session.sessionId,
            session.campaignId,
            session.userId,
            session.status,
            session.startedAt,
            session.completedAt,
            dur,
            session.score,
            session.totalStages,
            stage?.stageOrder ?? '',
            stage?.questionId ?? '',
            stage?.responseType ?? '',
            stage?.selectedChoiceId ?? '',
            Array.isArray(stage?.selectedChoiceIds) ? stage!.selectedChoiceIds!.join('|') : '',
            stage?.textResponse ?? '',
            stage?.ratingValue ?? '',
            typeof stage?.booleanValue === 'boolean' ? String(stage.booleanValue) : '',
            typeof stage?.correct === 'boolean' ? String(stage.correct) : '',
            stage?.recordedAt ?? '',
          ].map(csvEscape).join(','),
        )
      }
    }

    triggerDownload(lines.join('\n'), 'responses-long')
  } catch (err) {
    console.warn('[SurveyResponsesPanel] Long CSV download failed', err)
    downloadError.value = 'Unable to generate CSV right now.'
  } finally {
    isDownloading.value = false
  }
}

// ─── Watchers ─────────────────────────────────────────────────────────────────

watch(
  () => props.campaignId,
  (id) => {
    if (id) refresh()
    else {
      sessions.value = []
      errorMessage.value = ''
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.srp {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.srp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.srp-header-info {
  font-size: 0.88rem;
  min-height: 1.5rem;
}

.srp-muted {
  opacity: 0.7;
  font-style: italic;
}

.srp-error {
  color: #ff6b6b;
  font-weight: 600;
}

.srp-download-error {
  font-size: 0.85rem;
}

.srp-count {
  opacity: 0.8;
}

.srp-table-scroll {
  overflow-x: auto;
  /* Darker blue tile colour, matching the Campaign Insights inner sections. */
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

.srp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
  min-width: 680px;
}

.srp-table th,
.srp-table td {
  padding: 0.5rem 0.7rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  text-align: left;
  vertical-align: top;
  white-space: nowrap;
}

.srp-table thead th {
  position: sticky;
  top: 0;
  /* Opaque darker-blue header strip so rows don't show through when scrolling. */
  background: rgb(20, 31, 49);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.85;
  z-index: 1;
}

.srp-sort-btn {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.3rem;
  width: 100%;
  padding: 0;
  margin: 0;
  background: transparent;
  border: none;
  box-shadow: none;
  color: inherit;
  font: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  text-align: left;
  cursor: pointer;
}

.srp-sort-btn:hover {
  color: var(--vt-c-white);
}

.srp-sort-ind {
  font-size: 0.72em;
  /* Solid (not greyed) so every column reads as sortable. */
  opacity: 1;
  color: var(--vt-c-white, #ffffff);
  transition: color 0.12s ease;
}

/* Active column's arrow uses the accent colour to show the live sort + order. */
.srp-sort-ind--active {
  color: var(--vt-c-accent, rgb(150, 255, 201));
}

.srp-session-row { cursor: pointer; }
.srp-session-row:hover { background: rgba(255, 255, 255, 0.03); }

.srp-toggle-cell {
  width: 2rem;
  text-align: center;
}

.srp-chev {
  display: inline-block;
  transition: transform 150ms ease;
  opacity: 0.7;
}

.srp-chev--open {
  transform: rotate(90deg);
  opacity: 1;
}

.srp-detail-cell {
  background: rgba(255, 255, 255, 0.02);
  padding: 0.75rem 1rem;
  white-space: normal;
}

.srp-stages {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.srp-round-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.55;
  margin: 0.4rem 0 0.1rem;
}

.srp-stage {
  padding: 0.5rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.02);
}

.srp-stage-head {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.82rem;
  margin-bottom: 0.3rem;
}

.srp-stage-meta {
  opacity: 0.65;
}

.srp-stage-body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.85rem;
}

.srp-stage-q {
  opacity: 0.75;
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.78rem;
}

.srp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.srp-footer-note {
  margin: 0;
  font-size: 0.78rem;
  opacity: 0.6;
  font-style: italic;
  flex: 1 1 16rem;
}

.srp-footer-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
}
</style>
